// Admin org management - provisioning + request approval (staff only).
import { supabase } from '@govern360/shared';

// Console site hosts the Netlify functions. Admin calls send-invite cross-origin.
const FUNCTIONS_BASE = 'https://console.govern360.ai/.netlify/functions';

// Create an org + optionally invite the first user (owner).
// Returns jsonb: { org, invitation_id, invitation_token, owner_attached, owner_email }.
export async function provisionOrg({ name, industry, size, plan, ownerEmail }) {
  const { data, error } = await supabase.rpc('staff_provision_org', {
    p_name: name,
    p_industry: industry || null,
    p_size: size || null,
    p_plan: plan || 'trial',
    p_owner_email: ownerEmail || null,
  });
  return { data, error };
}

// Send the branded invite email (Postmark) for an existing invitation row.
export async function sendInviteEmail(invitationId) {
  // Use getSession() (NOT a raw localStorage read): it auto-refreshes an expired
  // access token via the stored refresh token. A raw read sends whatever string is
  // in storage even if it died days ago -> "Invalid token" on the server. If the
  // session is missing/unrefreshable, surface a clear re-login message.
  let { data: { session } } = await supabase.auth.getSession();
  // If the access token is at/near expiry, force a refresh before sending.
  const expMs = session?.expires_at ? session.expires_at * 1000 : 0;
  if (session && expMs && expMs < Date.now() + 30000) {
    const r = await supabase.auth.refreshSession();
    if (!r.error && r.data?.session) session = r.data.session;
  }
  const accessToken = session?.access_token || null;
  if (!accessToken) {
    return { error: { message: 'Your session has expired. Please sign out and sign in again.' } };
  }
  try {
    const res = await fetch(`${FUNCTIONS_BASE}/send-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ invitationId }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) return { error: { message: j.error || `Send failed (${res.status})`, detail: j.detail } };
    return { data: j };
  } catch (e) {
    return { error: { message: e.message } };
  }
}

// Org requests (the approval queue).
export async function listOrgRequests() {
  const { data, error } = await supabase
    .from('org_requests').select('*').order('created_at', { ascending: false });
  return { data: data || [], error };
}
export async function approveOrgRequest(id) {
  const { data, error } = await supabase.rpc('approve_org_request', { p_request_id: id });
  return { data, error };
}
export async function denyOrgRequest(id) {
  const { error } = await supabase.rpc('deny_org_request', { p_request_id: id });
  return { error };
}

export async function staffListOrgs() {
  const { data, error } = await supabase.rpc('staff_list_orgs');
  return { data: data || [], error };
}
export async function staffUpdateOrg(orgId, o) {
  const { error } = await supabase.rpc('staff_update_org', {
    p_org_id: orgId, p_name: o.name, p_industry: o.industry, p_size: o.size,
    p_plan: o.plan, p_primary_color: o.primary_color || null, p_slug: o.slug || null,
  });
  return { error };
}
export async function staffGetBilling(orgId) {
  const { data, error } = await supabase.rpc('staff_get_billing', { p_org_id: orgId });
  return { data, error };
}
export async function staffSetBilling(orgId, b) {
  const { error } = await supabase.rpc('staff_set_billing', {
    p_org_id: orgId, p_tier: b.tier,
    p_monthly_min: b.monthly_min === '' ? null : Number(b.monthly_min),
    p_rate_per_tau: b.rate_per_tau === '' ? null : Number(b.rate_per_tau),
    p_token_allowance: b.token_allowance === '' ? null : Number(b.token_allowance),
    p_overage_rate: b.overage_rate === '' ? null : Number(b.overage_rate),
    p_contract_end: b.contract_end || null,
    p_billing_email: b.billing_email || null,
  });
  return { error };
}
export async function staffSetOrgStatus(orgId, status) {
  const { error } = await supabase.rpc('staff_set_org_status', { p_org_id: orgId, p_status: status });
  return { error };
}
export async function staffDeleteOrg(orgId) {
  const { error } = await supabase.rpc('staff_delete_org', { p_org_id: orgId });
  return { error };
}

export async function staffListInvitations(orgId) {
  const { data, error } = await supabase.rpc('staff_list_invitations', { p_org_id: orgId });
  return { data: data || [], error };
}

export async function staffListMembers(orgId) {
  const { data, error } = await supabase.rpc('staff_list_members', { p_org_id: orgId });
  return { data: data || [], error };
}
export async function staffSetMemberRole(orgId, userId, role) {
  const { error } = await supabase.rpc('staff_set_member_role', { p_org_id: orgId, p_user_id: userId, p_role: role });
  return { error };
}
export async function staffRemoveMember(orgId, userId) {
  const { error } = await supabase.rpc('staff_remove_member', { p_org_id: orgId, p_user_id: userId });
  return { error };
}